import { useTranslation } from 'react-i18next';

import { forwardRef, Text, VStack } from '@bonusly/design-system';

import { useActionInput } from '../../context';
import { Budget } from '../../lib/entities/budget';
import { SelectorBox, SelectorProps } from '../selector/selector-box';
import { SelectorOption } from '../selector/selector-option';
import { useSelector } from '../selector/use-selector';
import { AmountBalance } from './amount-balance';
import { FlashPointsCountdown } from './flash-points-countdown';

interface AmountSelectorProps extends SelectorProps<number> {
	allowanceBoostId?: string;
	budget: Budget;
}

export const AmountSelector = forwardRef<AmountSelectorProps, 'div'>(({ allowanceBoostId, budget, command, hash, items }, ref) => {
	const { t } = useTranslation('components');
	const { context } = useActionInput();
	const { getOptionProps, getSelectorProps, selectedIndex } = useSelector({
		command,
		hash,
		items,
		ref,
	});

	const isAwardContext = context.includes('award');

	if (!items.length) {
		return null;
	}

	return (
		<SelectorBox minW="auto">
			{!isAwardContext && <FlashPointsCountdown />}
			<VStack spacing={0} align="stretch" {...getSelectorProps()}>
				{budget.hasAllowance && <AmountBalance allowanceBoostId={allowanceBoostId} budget={budget} />}

				{items.map((amount, index) => (
					<SelectorOption
						key={amount}
						isSelected={selectedIndex === index}
						aria-label={t('rich_media.amount.select_amount', { amount })}
						_hover={{ backgroundColor: 'points.bgAlpha' }}
						sx={{
							'&[data-focus="true"]': {
								backgroundColor: 'points.bgAlpha',
							},
						}}
						{...getOptionProps(index)}
					>
						<Text fontSize="md" fontWeight="medium" lineHeight={6} color="points.fg">
							+{budget.format(amount)}
						</Text>
					</SelectorOption>
				))}
			</VStack>
		</SelectorBox>
	);
});
